import { PartFinderBatch } from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { getBatchLabel } from '@/components/PartFinder/batchLabel';
import { useDeletePartFinderBatches } from '@/hooks/api/part-finder-batches/useDeletePartFinderBatches';
import { useToast } from '@/hooks/useToast';
import { pluralize } from '@/utils/format';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useState } from 'react';

export interface DeleteBatchModalProperties {
  batch: PartFinderBatch;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}

export const DeleteBatchModal = ({ batch, open, onClose, onDeleted }: DeleteBatchModalProperties) => {
  const { showToast } = useToast();
  const [errorMessage, setErrorMessage] = useState<string>();
  const { mutate, isPending } = useDeletePartFinderBatches({
    onSuccess: () => {
      showToast({ message: 'Batch deleted', type: 'success' });
      onDeleted?.();
      onClose();
    },
    onError: setErrorMessage,
  });

  const reportCount = batch.breakdown.total;

  const handleClose = () => {
    if (isPending) return;
    setErrorMessage(undefined);
    onClose();
  };

  const handleConfirm = () => {
    setErrorMessage(undefined);
    mutate([batch.id]);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>Delete batch</DialogTitle>
      <DialogContent>
        <ApiErrorAlert error={errorMessage ? { message: errorMessage } : undefined} />
        <DialogContentText>
          Delete batch &ldquo;{getBatchLabel(batch)}&rdquo;? This also deletes all of its{' '}
          {pluralize(reportCount, 'report', 'reports')}. This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending} color='inherit'>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isPending}
          variant='contained'
          color='error'
          startIcon={isPending ? <CircularProgress size={16} color='inherit' /> : undefined}
        >
          Delete batch
        </Button>
      </DialogActions>
    </Dialog>
  );
};
